import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useWarehouses } from '../../hooks/useWarehouses';
import { authenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { fetchLocationsForAdmin } from '../../utils/locationInventory';
import { syncLocationsFromShopify } from '../../utils/shopifyLocationSync';
import { runShopifyLinkAll, formatUnlinkedReason, type ShopifyLinkAllResult } from '../../utils/shopifyLinkAll';
import { getAdminId } from '../../utils/getAdminId';
import { Button } from '../ui/Button';
import {
  MapPin,
  RefreshCw,
  Save,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2,
  Link2,
  Unlink,
  Building,
  Store,
  Mail,
  Download,
} from 'lucide-react';

interface ShopifyLocation {
  id: string;
  name: string;
  isActive: boolean;
}

interface LocationMapping {
  id: string;
  shopify_location_id: bigint;
  shopify_location_name: string;
  warehouse_id: string | null;
  product_location_id?: string | null;
  is_active: boolean;
}

interface ShopifyStore {
  id: string;
  shop_domain: string;
  admin_id: string;
  is_active: boolean;
}

export const ShopifyLocationMapping: React.FC = () => {
  const { user } = useAuth();
  const { warehouses, loading: warehousesLoading, fetchWarehouses } = useWarehouses();

  const [shopifyStore, setShopifyStore] = useState<ShopifyStore | null>(null);
  const [shopifyLocations, setShopifyLocations] = useState<ShopifyLocation[]>([]);
  const [existingMappings, setExistingMappings] = useState<LocationMapping[]>([]);
  const [mappings, setMappings] = useState<Map<string, string | null>>(new Map());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [productLocations, setProductLocations] = useState<Array<{ id: string; name: string }>>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [linking, setLinking] = useState(false);
  const [linkProgress, setLinkProgress] = useState<string | null>(null);
  const [linkResult, setLinkResult] = useState<ShopifyLinkAllResult | null>(null);
  const [showUnlinkedList, setShowUnlinkedList] = useState(false);
  const [showLinkConfirm, setShowLinkConfirm] = useState(false);
  const [pickSheetEnabled, setPickSheetEnabled] = useState(false);
  const [pickSheetEmail, setPickSheetEmail] = useState('');
  const [savingPickSheetSettings, setSavingPickSheetSettings] = useState(false);
  const [backfillingFulfillable, setBackfillingFulfillable] = useState(false);
  const [backfillingPickup, setBackfillingPickup] = useState(false);

  const adminId = getAdminId(user);

  // Locations to show in dropdown: product_locations (for inventory) + warehouses (for compatibility)
  const appLocations = productLocations.length > 0 ? productLocations : warehouses.map((w) => ({ id: w.id, name: w.name }));

  const fetchPickSheetSettings = useCallback(async () => {
    if (!adminId) return;

    const { data, error: settingsError } = await supabase
      .from('shopify_settings')
      .select('pick_sheet_enabled, pick_sheet_email')
      .eq('admin_id', adminId)
      .maybeSingle();

    if (settingsError) {
      console.error('Error loading pick sheet settings:', settingsError);
      return;
    }

    setPickSheetEnabled(Boolean(data?.pick_sheet_enabled));
    setPickSheetEmail(data?.pick_sheet_email || '');
  }, [adminId]);

  useEffect(() => {
    void fetchPickSheetSettings();
  }, [fetchPickSheetSettings]);

  const handleSavePickSheetSettings = async () => {
    if (!adminId) return;

    const trimmedEmail = pickSheetEmail.trim();
    if (pickSheetEnabled && trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError('Enter a valid pick sheet email address');
      return;
    }

    setSavingPickSheetSettings(true);
    setError(null);

    try {
      const { error: saveError } = await supabase
        .from('shopify_settings')
        .upsert(
          {
            admin_id: adminId,
            pick_sheet_enabled: pickSheetEnabled && trimmedEmail.length > 0,
            pick_sheet_email: trimmedEmail || null,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'admin_id' }
        );

      if (saveError) throw saveError;

      setSuccess('Pick sheet email settings saved');
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save pick sheet settings');
    } finally {
      setSavingPickSheetSettings(false);
    }
  };

  const handleBackfillFulfillable = async () => {
    if (!adminId || backfillingFulfillable) return;

    setBackfillingFulfillable(true);
    setError(null);

    try {
      let totalProcessed = 0;
      let totalUpdated = 0;
      let totalFailed = 0;
      let hasMore = true;
      let guard = 0;

      while (hasMore && guard < 50) {
        guard++;
        const { data, error: backfillError, ok } = await authenticatedFetch<{
          processed: number;
          updated: number;
          failed: number;
          hasMore: boolean;
        }>(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-backfill-fulfillable`, {
          method: 'POST',
          body: JSON.stringify({ limit: 50 }),
          retryOnAuthFailure: true,
          maxRetries: 2,
        });

        if (!ok || !data) throw new Error(backfillError || 'Backfill failed');

        totalProcessed += data.processed;
        totalUpdated += data.updated;
        totalFailed += data.failed;
        hasMore = data.hasMore && data.processed > 0;
      }

      setSuccess(
        `Backfill complete: ${totalUpdated} order(s) updated, ${totalProcessed} processed${
          totalFailed ? `, ${totalFailed} failed` : ''
        }`
      );
      setTimeout(() => setSuccess(null), 6000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to backfill partial orders');
    } finally {
      setBackfillingFulfillable(false);
    }
  };

  const handleBackfillPickup = async () => {
    if (!adminId || backfillingPickup) return;

    setBackfillingPickup(true);
    setError(null);

    try {
      let totalProcessed = 0;
      let totalReclassified = 0;
      let totalFailed = 0;
      let hasMore = true;
      let guard = 0;

      while (hasMore && guard < 50) {
        guard++;
        const { data, error: backfillError, ok } = await authenticatedFetch<{
          processed: number;
          reclassified: number;
          failed: number;
          hasMore: boolean;
        }>(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-backfill-pickup`, {
          method: 'POST',
          body: JSON.stringify({ limit: 50 }),
          retryOnAuthFailure: true,
          maxRetries: 2,
        });

        if (!ok || !data) throw new Error(backfillError || 'Pickup backfill failed');

        totalProcessed += data.processed;
        totalReclassified += data.reclassified;
        totalFailed += data.failed;
        hasMore = data.hasMore && data.processed > 0;
      }

      setSuccess(
        `Pickup backfill complete: ${totalReclassified} order(s) moved to the Reservation Terminal, ${totalProcessed} checked${
          totalFailed ? `, ${totalFailed} failed` : ''
        }`
      );
      setTimeout(() => setSuccess(null), 6000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to backfill pickup orders');
    } finally {
      setBackfillingPickup(false);
    }
  };

  // Fetch Shopify store
  const fetchShopifyStore = useCallback(async () => {    if (!adminId) return;

    try {
      const { data, error: storeError } = await supabase
        .from('shopify_stores')
        .select('id, shop_domain, admin_id, is_active')
        .eq('admin_id', adminId)
        .eq('is_active', true)
        .maybeSingle();

      if (storeError) {
        console.error('Error fetching Shopify store:', storeError);
        setError('Failed to load Shopify connection');
        return;
      }

      setShopifyStore(data);
    } catch (err) {
      console.error('Error in fetchShopifyStore:', err);
      setError('Failed to load Shopify connection');
    }
  }, [adminId]);

  // Fetch Shopify locations
  const fetchShopifyLocations = useCallback(async () => {
    if (!shopifyStore) return;

    try {
      const result = await authenticatedFetch<{ locations: ShopifyLocation[] }>(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-push-inventory/locations`,
        {
          method: 'GET',
          retryOnAuthFailure: true,
          maxRetries: 2,
        }
      );

      if (!result.ok || result.error) {
        let errorMessage = result.error || 'Failed to fetch Shopify locations';
        
        // Provide more helpful error messages
        if (result.status === 401) {
          if (errorMessage.includes('signature') || errorMessage.includes('Invalid token')) {
            errorMessage = 'Authentication failed: invalid token signature. Please log out and log in again.';
          } else if (errorMessage.includes('expired')) {
            errorMessage = 'Session expired. Please log out and log in again.';
          } else {
            errorMessage = 'Authentication failed. Please log out and log in again.';
          }
        }
        
        setError(errorMessage);
        console.error('Error fetching Shopify locations:', {
          status: result.status,
          error: result.error,
          data: result.data
        });
        return;
      }

      if (result.data && 'locations' in result.data) {
        setShopifyLocations(result.data.locations || []);
        setError(null);
      } else {
        setError('Invalid response format from server');
      }
    } catch (err) {
      console.error('Error fetching Shopify locations:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch Shopify locations');
    }
  }, [shopifyStore]);

  // Fetch product_locations (for inventory sync)
  const fetchProductLocations = useCallback(async () => {
    if (!adminId) return;
    const locs = await fetchLocationsForAdmin(adminId);
    setProductLocations(locs);
  }, [adminId]);

  // Fetch existing mappings
  const fetchExistingMappings = useCallback(async () => {
    if (!shopifyStore || !adminId) return;

    try {
      const { data, error: mappingError } = await supabase
        .from('shopify_location_mappings')
        .select('id, shopify_location_id, shopify_location_name, warehouse_id, product_location_id, is_active')
        .eq('admin_id', adminId)
        .eq('shop_id', shopifyStore.id)
        .order('shopify_location_name', { ascending: true });

      if (mappingError) {
        console.error('Error fetching location mappings:', mappingError);
        setError('Failed to load location mappings');
        return;
      }

      setExistingMappings(data || []);
      
      // Initialize mappings state - use product_location_id when available, else warehouse_id
      const mappingsMap = new Map<string, string | null>();
      (data || []).forEach((mapping) => {
        const locationId = mapping.shopify_location_id.toString();
        const appLocationId = mapping.product_location_id ?? mapping.warehouse_id;
        mappingsMap.set(locationId, appLocationId);
      });
      setMappings(mappingsMap);
    } catch (err) {
      console.error('Error in fetchExistingMappings:', err);
      setError('Failed to load location mappings');
    }
  }, [shopifyStore, adminId]);

  // Load all data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      
      await fetchShopifyStore();
      await fetchWarehouses();
      await fetchProductLocations();
      
      setLoading(false);
    };

    if (adminId) {
      loadData();
    }
  }, [adminId, fetchShopifyStore, fetchWarehouses, fetchProductLocations]);

  // Fetch locations and mappings when store is available
  useEffect(() => {
    if (shopifyStore && !loading) {
      fetchShopifyLocations();
      fetchExistingMappings();
    }
  }, [shopifyStore, loading, fetchShopifyLocations, fetchExistingMappings]);

  // Handle mapping change
  const handleMappingChange = (shopifyLocationId: string, warehouseId: string | null) => {
    setMappings((prev) => {
      const newMap = new Map(prev);
      newMap.set(shopifyLocationId, warehouseId);
      return newMap;
    });
    setSuccess(null);
  };

  // Resolve app location id to product_location_id (for inventory sync)
  const getProductLocationId = (appLocationId: string | null): string | null => {
    if (!appLocationId) return null;
    const fromProduct = productLocations.find((p) => p.id === appLocationId);
    if (fromProduct) return fromProduct.id;
    const fromWarehouse = warehouses.find((w) => w.id === appLocationId);
    if (fromWarehouse) {
      const pl = productLocations.find((p) => p.name === fromWarehouse.name);
      return pl?.id ?? null;
    }
    return appLocationId;
  };

  // Save mappings
  const handleSaveMappings = async () => {
    if (!shopifyStore || !adminId) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const updates: PromiseLike<any>[] = [];

      for (const [shopifyLocationId, appLocationId] of mappings.entries()) {
        const locationId = Number(shopifyLocationId);
        const shopifyLocation = shopifyLocations.find((loc) => loc.id === shopifyLocationId);
        const locationName = shopifyLocation?.name || 'Unknown Location';
        const productLocationId = getProductLocationId(appLocationId);
        const warehouseId = warehouses.find((w) => w.id === appLocationId)?.id ?? null;

        const existingMapping = existingMappings.find(
          (m) => m.shopify_location_id.toString() === shopifyLocationId
        );

        if (existingMapping) {
          const changed =
            existingMapping.product_location_id !== productLocationId ||
            existingMapping.warehouse_id !== warehouseId;
          if (changed) {
            updates.push(
              supabase
                .from('shopify_location_mappings')
                .update({
                  product_location_id: productLocationId,
                  warehouse_id: warehouseId,
                  is_active: true,
                  updated_at: new Date().toISOString(),
                })
                .eq('id', existingMapping.id)
                .then()
            );
          }
        } else if (appLocationId) {
          updates.push(
            supabase
              .from('shopify_location_mappings')
              .insert({
                admin_id: adminId,
                shop_id: shopifyStore.id,
                shopify_location_id: locationId,
                shopify_location_name: locationName,
                product_location_id: productLocationId,
                warehouse_id: warehouseId,
                is_active: true,
                is_primary: false,
              })
              .then()
          );
        }
      }

      for (const mapping of existingMappings) {
        const locationId = mapping.shopify_location_id.toString();
        if (!mappings.has(locationId) && (mapping.warehouse_id !== null || mapping.product_location_id !== null)) {
          updates.push(
            supabase
              .from('shopify_location_mappings')
              .update({
                product_location_id: null,
                warehouse_id: null,
                updated_at: new Date().toISOString(),
              })
              .eq('id', mapping.id)
              .then()
          );
        }
      }

      await Promise.all(updates);
      await fetchExistingMappings();
      setSuccess('Location mappings saved successfully');
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('Error saving mappings:', err);
      setError(err instanceof Error ? err.message : 'Failed to save location mappings');
    } finally {
      setSaving(false);
    }
  };

  // Refresh locations from Shopify
  const handleRefreshLocations = async () => {
    setRefreshing(true);
    setError(null);
    
    try {
      await fetchShopifyLocations();
      await fetchExistingMappings();
      await fetchProductLocations();
      await fetchWarehouses();
      setSuccess('Locations refreshed successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error refreshing locations:', err);
      setError(err instanceof Error ? err.message : 'Failed to refresh locations');
    } finally {
      setRefreshing(false);
    }
  };

  // Sync locations from Shopify - creates product_locations, warehouses, mappings
  const handleSyncFromShopify = async () => {
    if (!adminId || !shopifyStore || shopifyLocations.length === 0) return;

    setSyncing(true);
    setError(null);

    try {
      const result = await syncLocationsFromShopify(shopifyLocations, adminId, shopifyStore.id);

      if (!result.success) {
        setError(result.error || 'Sync failed');
        return;
      }

      await fetchProductLocations();
      await fetchWarehouses();
      await fetchExistingMappings();
      setSuccess(`Synced ${result.createdCount} location(s) from Shopify. Each location now has its own inventory level.`);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('Error syncing locations:', err);
      setError(err instanceof Error ? err.message : 'Failed to sync locations from Shopify');
    } finally {
      setSyncing(false);
    }
  };

  const handleLinkAllProducts = async () => {
    setShowLinkConfirm(false);
    setLinking(true);
    setLinkProgress('Linking products to Shopify…');
    setLinkResult(null);
    setError(null);

    try {
      const result = await runShopifyLinkAll({
        onProgress: (part, partial) => {
          setLinkProgress(
            part === 1
              ? `Linking batch ${part}… ${partial.linked} linked, ${partial.unlinked_skus.length} unlinked so far`
              : `Linking batch ${part}… ${partial.linked} linked, ${partial.unlinked_skus.length} unlinked so far`
          );
          setLinkResult(partial);
        },
      });

      setLinkResult(result);
      setLinkProgress(null);
      setShowUnlinkedList(result.unlinked_skus.length > 0);
      setSuccess(
        `Linked ${result.linked} product${result.linked !== 1 ? 's' : ''} to Shopify. ` +
          `${result.unlinked_skus.length} SKU${result.unlinked_skus.length !== 1 ? 's' : ''} could not be linked. ` +
          `Local quantities were not changed.`
      );
      setTimeout(() => setSuccess(null), 10000);
    } catch (err) {
      console.error('Error linking products:', err);
      setLinkProgress(null);
      setError(err instanceof Error ? err.message : 'Failed to link products to Shopify');
    } finally {
      setLinking(false);
    }
  };

  // Check if mappings have changed
  const hasChanges = () => {
    for (const [locationId, appLocationId] of mappings.entries()) {
      const existing = existingMappings.find(
        (m) => m.shopify_location_id.toString() === locationId
      );
      const existingAppId = existing?.product_location_id ?? existing?.warehouse_id;
      if (!existing || existingAppId !== appLocationId) {
        return true;
      }
    }
    for (const mapping of existingMappings) {
      const locationId = mapping.shopify_location_id.toString();
      const hasMapping = mapping.product_location_id !== null || mapping.warehouse_id !== null;
      if (hasMapping && !mappings.has(locationId)) return true;
    }
    return false;
  };

  if (loading || warehousesLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-cyan-600 mx-auto mb-4" />
          <p className="text-sm text-gray-600 dark:text-gray-400">Loading location settings...</p>
        </div>
      </div>
    );
  }

  if (!shopifyStore) {
    return (
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <AlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-300 mb-2">
              No Shopify Connection
            </h3>
            <p className="text-sm text-amber-700 dark:text-amber-400">
              Please connect your Shopify store first before configuring location mappings.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // No locations configured - offer Sync from Shopify when we have Shopify locations
  if (appLocations.length === 0 && shopifyLocations.length > 0) {
    return (
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <Building className="h-6 w-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-300 mb-2">
              No App Locations Configured
            </h3>
            <p className="text-sm text-blue-700 dark:text-blue-400 mb-4">
              Sync your Shopify locations to create matching app locations. Each location will have its own inventory level—reductions in Shopify Location 1 will only affect App Location 1, and so on.
            </p>
            <Button
              variant="primary"
              onClick={handleSyncFromShopify}
              disabled={syncing}
            >
              {syncing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Syncing…
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Sync from Shopify
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (appLocations.length === 0) {
    return (
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <Building className="h-6 w-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-300 mb-2">
              No Locations Found
            </h3>
            <p className="text-sm text-blue-700 dark:text-blue-400">
              No Shopify locations to sync. Ensure your Shopify store has locations configured, then use Refresh Locations.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
            Location Mapping
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Map your App Locations (warehouses) to Shopify Locations for inventory synchronization.
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleRefreshLocations}
          disabled={refreshing}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh Locations
        </Button>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
            <p className="text-sm text-green-800 dark:text-green-300">{success}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
            <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      {/* Pick Sheet Email */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start gap-3 mb-4">
          <Mail className="h-5 w-5 text-cyan-600 dark:text-cyan-400 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Pick Sheet Email
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              When a paid order appears in Worker Terminal, a pick sheet PDF (SKU, color, size, bin location) is emailed automatically.
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-end gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={pickSheetEnabled}
              onChange={(e) => setPickSheetEnabled(e.target.checked)}
              className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
            />
            Enable automatic pick sheet emails
          </label>
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Recipient email
            </label>
            <input
              type="email"
              value={pickSheetEmail}
              onChange={(e) => setPickSheetEmail(e.target.value)}
              placeholder="warehouse@example.com"
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
            />
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleSavePickSheetSettings}
            disabled={savingPickSheetSettings}
          >
            {savingPickSheetSettings ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save
          </Button>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Run once after enabling partial-fulfillment support: refreshes existing
            partially fulfilled orders so pick lists only show unfulfilled items.
          </p>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleBackfillFulfillable}
            disabled={backfillingFulfillable}
          >
            {backfillingFulfillable ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Backfill partial orders
          </Button>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Move existing "pickup"-tagged Shopify orders out of the Worker Terminal and into the
            Reservation Terminal as store pickups. Safe to run repeatedly.
          </p>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleBackfillPickup}
            disabled={backfillingPickup}
          >
            {backfillingPickup ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Backfill pickup orders
          </Button>
        </div>
      </div>

      {/* Link All Products */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Link2 className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Link Products to Shopify
              </h4>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Connect all SKU-matched products to Shopify so quantity changes and orders sync automatically via webhooks.
              This does not change your current local quantities.
            </p>
            {linkProgress && (
              <p className="text-sm text-cyan-700 dark:text-cyan-300 flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                {linkProgress}
              </p>
            )}
            {linkResult && !linking && (
              <div className="mt-3 space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-2 border border-green-200 dark:border-green-800">
                    <div className="font-semibold text-green-800 dark:text-green-300">{linkResult.linked}</div>
                    <div className="text-green-700 dark:text-green-400">Linked</div>
                  </div>
                  <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-2 border border-amber-200 dark:border-amber-800">
                    <div className="font-semibold text-amber-800 dark:text-amber-300">{linkResult.unlinked_skus.length}</div>
                    <div className="text-amber-700 dark:text-amber-400">Unlinked SKUs</div>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-2 border border-red-200 dark:border-red-800">
                    <div className="font-semibold text-red-800 dark:text-red-300">{linkResult.duplicates_in_supabase.length}</div>
                    <div className="text-red-700 dark:text-red-400">Duplicate SKUs</div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900/40 rounded-lg p-2 border border-gray-200 dark:border-gray-700">
                    <div className="font-semibold text-gray-800 dark:text-gray-200">{linkResult.without_sku_count}</div>
                    <div className="text-gray-600 dark:text-gray-400">No SKU</div>
                  </div>
                </div>

                {linkResult.unlinked_skus.length > 0 && (
                  <div className="border border-amber-200 dark:border-amber-800 rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/20">
                      <button
                        type="button"
                        onClick={() => setShowUnlinkedList(!showUnlinkedList)}
                        className="text-sm font-medium text-amber-900 dark:text-amber-200 text-left"
                      >
                        {showUnlinkedList ? 'Hide' : 'Show'} unlinked SKUs ({linkResult.unlinked_skus.length})
                      </button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          const text = linkResult.unlinked_skus
                            .map(e => `${e.sku}\t${formatUnlinkedReason(e.reason)}`)
                            .join('\n');
                          void navigator.clipboard.writeText(text);
                        }}
                      >
                        Copy list
                      </Button>
                    </div>
                    {showUnlinkedList && (
                      <div className="max-h-48 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-50 dark:bg-gray-900/50 sticky top-0">
                            <tr>
                              <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">SKU</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Reason</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                            {linkResult.unlinked_skus.map(entry => (
                              <tr key={entry.product_id}>
                                <td className="px-3 py-1.5 font-mono text-gray-800 dark:text-gray-200">{entry.sku}</td>
                                <td className="px-3 py-1.5 text-gray-600 dark:text-gray-400">
                                  {formatUnlinkedReason(entry.reason)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <Button
            variant="secondary"
            onClick={() => setShowLinkConfirm(true)}
            disabled={linking || !shopifyStore}
            loading={linking}
            className="shrink-0 border-cyan-200 dark:border-cyan-700 text-cyan-700 dark:text-cyan-300"
          >
            <Link2 className="h-4 w-4 mr-2" />
            Link All Products
          </Button>
        </div>

        {showLinkConfirm && (
          <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <p className="text-sm text-amber-900 dark:text-amber-200 mb-3">
              This links all SKU-matched products to Shopify so future Shopify quantity and order changes sync automatically.
              It does <strong>not</strong> change your current local quantities.
            </p>
            <div className="flex gap-2">
              <Button variant="primary" size="sm" onClick={handleLinkAllProducts}>
                Confirm Link All
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setShowLinkConfirm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Mapping Table */}
      {shopifyLocations.length === 0 ? (
        <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-8 text-center">
          <Store className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">
            No Shopify locations found. Make sure your Shopify store has locations configured.
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Shopify Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    App Location (Warehouse)
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {shopifyLocations.map((location) => {
                  const currentMapping = mappings.get(location.id) || null;
                  const mappedLocation = currentMapping
                    ? appLocations.find((a) => a.id === currentMapping)
                    : null;
                  const isMapped = currentMapping !== null;

                  return (
                    <tr
                      key={location.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-2 h-2 rounded-full ${
                              location.isActive
                                ? 'bg-green-500'
                                : 'bg-gray-400'
                            }`}
                          />
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {location.name}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              ID: {location.id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <select
                          value={currentMapping || ''}
                          onChange={(e) =>
                            handleMappingChange(
                              location.id,
                              e.target.value || null
                            )
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                        >
                          <option value="">-- Not Mapped --</option>
                          {appLocations.map((loc) => (
                            <option key={loc.id} value={loc.id}>
                              {loc.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {isMapped ? (
                          <div className="flex items-center justify-center gap-2 text-green-600 dark:text-green-400">
                            <Link2 className="h-4 w-4" />
                            <span className="text-xs font-medium">Mapped</span>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center gap-2 text-gray-400">
                            <Unlink className="h-4 w-4" />
                            <span className="text-xs">Unmapped</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Save Button */}
      <div className="flex justify-end gap-3">
        <Button
          variant="primary"
          onClick={handleSaveMappings}
          disabled={saving || !hasChanges()}
          loading={saving}
        >
          <Save className="h-4 w-4 mr-2" />
          Save Mappings
        </Button>
      </div>

      {/* Info Box */}
      <div className="bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 p-6 rounded-xl border border-cyan-200 dark:border-cyan-800">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
            <MapPin className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
              About Location Mapping
            </h4>
            <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
              <li>• Map each Shopify location to an App Location (warehouse) for inventory sync</li>
              <li>• When Shopify location changes occur, the mapping will be preserved</li>
              <li>• Unmapped locations will not sync inventory automatically</li>
              <li>• Use "Refresh Locations" to sync the latest locations from Shopify</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
